import { useState, useCallback } from 'react';
import {
  getSchoolUsersRequest,
  getSchoolManagersRequest,
  confirmPrintingRequest,
  updateUserNfcRequest,
  confirmAddEmailRequest,
  confirmPaperCatRequest,
  confirmEndAllRequest,
} from '../services/apiClient';

const idleStatus = { type: 'idle', message: '' };

export default function useSchoolUsers(token) {
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [status, setStatus] = useState(idleStatus);
  const [managersStatus, setManagersStatus] = useState(idleStatus);
  const [total, setTotal] = useState(0);
  const [managersTotal, setManagersTotal] = useState(0);
  const [actionStatus, setActionStatus] = useState(idleStatus);
  const [confirmingId, setConfirmingId] = useState(null);
  const [nfcStatus, setNfcStatus] = useState(idleStatus);
  const [nfcUpdatingId, setNfcUpdatingId] = useState(null);
  const [emailStatus, setEmailStatus] = useState(idleStatus);
  const [confirmingEmailId, setConfirmingEmailId] = useState(null);
  const [paperCatStatus, setPaperCatStatus] = useState(idleStatus);
  const [confirmingPaperCatId, setConfirmingPaperCatId] = useState(null);
  const [endStatus, setEndStatus] = useState(idleStatus);
  const [confirmingEndId, setConfirmingEndId] = useState(null);

  const resetState = useCallback(() => {
    setSelectedSchool(null);
    setUsers([]);
    setManagers([]);
    setStatus(idleStatus);
    setManagersStatus(idleStatus);
    setTotal(0);
    setManagersTotal(0);
    setActionStatus(idleStatus);
    setConfirmingId(null);
    setNfcStatus(idleStatus);
    setNfcUpdatingId(null);
    setEmailStatus(idleStatus);
    setConfirmingEmailId(null);
    setPaperCatStatus(idleStatus);
    setConfirmingPaperCatId(null);
    setEndStatus(idleStatus);
    setConfirmingEndId(null);
  }, []);

  const fetchUsers = useCallback(
    async (school) => {
      if (!token || !school) {
        resetState();
        return;
      }

      const schoolId = school?._id || school?.id || school?.schoolId;
      if (!schoolId) {
        setStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المدرسة المختارة.',
        });
        return;
      }

      setSelectedSchool(school);
      setStatus({
        type: 'loading',
        message: 'جاري تحميل مستخدمي المدرسة المختارة...',
      });

      try {
        const {
          users: fetchedUsers,
          school: schoolDetails,
          total: fetchedTotal,
        } = await getSchoolUsersRequest({
          schoolId,
          token,
        });

        setUsers(fetchedUsers);
        setSelectedSchool(schoolDetails || school);
        setTotal(fetchedTotal || fetchedUsers.length);
        setStatus({ type: 'success', message: '' });
      } catch (error) {
        setUsers([]);
        setStatus({
          type: 'error',
          message: error.message || 'حدث خطأ أثناء جلب مستخدمي المدرسة.',
        });
      }
    },
    [token, resetState]
  );

  const fetchManagers = useCallback(
    async (school) => {
      if (!token || !school) {
        return;
      }

      const schoolId = school?._id || school?.id || school?.schoolId;
      if (!schoolId) {
        setManagersStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المدرسة المختارة.',
        });
        return;
      }

      setManagersStatus({
        type: 'loading',
        message: 'جاري تحميل إداريي المدرسة المختارة...',
      });

      try {
        const {
          users: fetchedManagers,
          total: fetchedTotal,
        } = await getSchoolManagersRequest({
          schoolId,
          token,
        });

        setManagers(fetchedManagers);
        setManagersTotal(fetchedTotal || fetchedManagers.length);
        setManagersStatus({ type: 'success', message: '' });
      } catch (error) {
        setManagers([]);
        setManagersStatus({
          type: 'error',
          message: error.message || 'حدث خطأ أثناء جلب إداريي المدرسة.',
        });
      }
    },
    [token]
  );

  const normalizePrintingDate = (value) => {
    if (!value) {
      return new Date().toISOString();
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return new Date().toISOString();
    }
    return parsed.toISOString();
  };

  const confirmPrinting = useCallback(
    async (userRecord, customDate) => {
      if (!token || !userRecord) {
        return;
      }
      const recordId =
        userRecord?._id || userRecord?.id || userRecord?.userId;
      if (!recordId) {
        setActionStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المستخدم المراد تأكيده.',
        });
        return;
      }

      setConfirmingId(recordId);
      setActionStatus({
        type: 'loading',
        message: 'جاري تأكيد طباعة بطاقة المستخدم...',
      });

      try {
        const printingDate = normalizePrintingDate(customDate);
        const result = await confirmPrintingRequest({
          recordId,
          token,
          printingDate,
        });

        setUsers((prev) =>
          prev.map((user) =>
            (user?._id || user?.id || user?.userId) === recordId
              ? {
                  ...user,
                  printingCheck: result?.printingCheck ?? true,
                  printingDate:
                    result?.printingDate || result?.printingCreatedAt || printingDate,
                  printingCreatedAt: result?.printingCreatedAt || printingDate,
                  userPrinted: result?.userPrinted || user?.userPrinted,
                }
              : user
          )
        );

        setManagers((prev) =>
          prev.map((user) =>
            (user?._id || user?.id || user?.userId) === recordId
              ? {
                  ...user,
                  printingCheck: result?.printingCheck ?? true,
                  printingDate:
                    result?.printingDate || result?.printingCreatedAt || printingDate,
                  printingCreatedAt: result?.printingCreatedAt || printingDate,
                  userPrinted: result?.userPrinted || user?.userPrinted,
                }
              : user
          )
        );

        setActionStatus({
          type: 'success',
          message: 'تم تأكيد طباعة البطاقة بنجاح.',
        });
      } catch (error) {
        setActionStatus({
          type: 'error',
          message: error.message || 'تعذر تأكيد طباعة بطاقة المستخدم.',
        });
      } finally {
        setConfirmingId(null);
      }
    },
    [token]
  );

  const resolveRecordIdentifier = (userRecord) =>
    userRecord?._id || userRecord?.id || userRecord?.userId;

  const resolveAccountIdentifier = (userRecord) => {
    if (!userRecord) {
      return null;
    }
    if (userRecord.user && typeof userRecord.user === 'object') {
      return userRecord.user._id || userRecord.user.id || null;
    }
    return (
      userRecord.user ||
      userRecord.userId ||
      userRecord.userIdNumber ||
      userRecord.accountId ||
      null
    );
  };

  const updateNfcId = useCallback(
    async (userRecord, nfcIdValue) => {
      if (!token || !userRecord) {
        return;
      }
      const recordId = resolveRecordIdentifier(userRecord);
      const userAccountId = resolveAccountIdentifier(userRecord);
      if (!recordId) {
        setNfcStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المستخدم لتحديث رمز NFC.',
        });
        return;
      }
      if (!userAccountId) {
        setNfcStatus({
          type: 'error',
          message: 'لا يمكن تحديد حساب المستخدم لتحديث رمز NFC.',
        });
        return;
      }
      if (!nfcIdValue || !nfcIdValue.trim()) {
        setNfcStatus({
          type: 'error',
          message: 'يرجى إدخال رمز NFC قبل التحديث.',
        });
        return;
      }

      setNfcUpdatingId(recordId);
      setNfcStatus({
        type: 'loading',
        message: 'جاري تحديث رمز NFC للمستخدم...',
      });

      try {
        await updateUserNfcRequest({
          userId: userAccountId,
          nfcId: nfcIdValue.trim(),
          token,
        });

        setUsers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? { ...user, nfcId: nfcIdValue.trim() }
              : user
          )
        );

        setManagers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? { ...user, nfcId: nfcIdValue.trim() }
              : user
          )
        );

        setNfcStatus({
          type: 'success',
          message: 'تم تحديث رمز NFC بنجاح.',
        });
      } catch (error) {
        setNfcStatus({
          type: 'error',
          message: error.message || 'تعذر تحديث رمز NFC للمستخدم.',
        });
      } finally {
        setNfcUpdatingId(null);
      }
    },
    [token]
  );

  const confirmAddEmail = useCallback(
    async (userRecord, customDate) => {
      if (!token || !userRecord) {
        return;
      }
      const recordId = resolveRecordIdentifier(userRecord);
      if (!recordId) {
        setEmailStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المستخدم لتأكيد البريد.',
        });
        return;
      }

      setConfirmingEmailId(recordId);
      setEmailStatus({
        type: 'loading',
        message: 'جاري تأكيد إضافة البريد للمستخدم...',
      });

      try {
        const addEmailDate = normalizePrintingDate(customDate);
        const result = await confirmAddEmailRequest({
          recordId,
          token,
          addEmailDate,
        });

        setUsers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? {
                  ...user,
                  AddEmailCheck: result?.AddEmailCheck ?? true,
                  AddEmailDate: result?.AddEmailDate || addEmailDate,
                  AddEmailCreatedAt: result?.AddEmailCreatedAt || addEmailDate,
                }
              : user
          )
        );

        setManagers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? {
                  ...user,
                  AddEmailCheck: result?.AddEmailCheck ?? true,
                  AddEmailDate: result?.AddEmailDate || addEmailDate,
                  AddEmailCreatedAt: result?.AddEmailCreatedAt || addEmailDate,
                }
              : user
          )
        );

        setEmailStatus({
          type: 'success',
          message: 'تم تأكيد إضافة البريد بنجاح.',
        });
      } catch (error) {
        setEmailStatus({
          type: 'error',
          message: error.message || 'تعذر تأكيد إضافة البريد.',
        });
      } finally {
        setConfirmingEmailId(null);
      }
    },
    [token]
  );

  const confirmPaperCat = useCallback(
    async (userRecord, customDate) => {
      if (!token || !userRecord) {
        return;
      }
      const recordId = resolveRecordIdentifier(userRecord);
      if (!recordId) {
        setPaperCatStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المستخدم لتأكيد papercut.',
        });
        return;
      }

      setConfirmingPaperCatId(recordId);
      setPaperCatStatus({
        type: 'loading',
        message: 'جاري تأكيد papercut للمستخدم...',
      });

      try {
        const paperCatDate = normalizePrintingDate(customDate);
        const result = await confirmPaperCatRequest({
          recordId,
          token,
          paperCatDate,
        });

        setUsers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? {
                  ...user,
                  paperCat: result?.paperCat ?? true,
                  paperCatDate: result?.paperCatDate || paperCatDate,
                  paperCatCreatedAt:
                    result?.paperCatCreatedAt || paperCatDate,
                }
              : user
          )
        );

        setManagers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? {
                  ...user,
                  paperCat: result?.paperCat ?? true,
                  paperCatDate: result?.paperCatDate || paperCatDate,
                  paperCatCreatedAt:
                    result?.paperCatCreatedAt || paperCatDate,
                }
              : user
          )
        );

        setPaperCatStatus({
          type: 'success',
          message: 'تم تأكيد papercut بنجاح.',
        });
      } catch (error) {
        setPaperCatStatus({
          type: 'error',
          message: error.message || 'تعذر تأكيد papercut.',
        });
      } finally {
        setConfirmingPaperCatId(null);
      }
    },
    [token]
  );

  const confirmEndAll = useCallback(
    async (userRecord, customDate) => {
      if (!token || !userRecord) {
        return;
      }
      const recordId = resolveRecordIdentifier(userRecord);
      if (!recordId) {
        setEndStatus({
          type: 'error',
          message: 'لا يمكن تحديد معرف المستخدم لتأكيد الإكمال النهائي.',
        });
        return;
      }

      setConfirmingEndId(recordId);
      setEndStatus({
        type: 'loading',
        message: 'جاري تأكيد الإكمال النهائي للمستخدم...',
      });

      try {
        const endCheckDate = normalizePrintingDate(customDate);
        const result = await confirmEndAllRequest({
          recordId,
          token,
          endCheckDate,
        });

        setUsers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? {
                  ...user,
                  endCheck: result?.endCheck ?? true,
                  endCheckDate: result?.endCheckDate || endCheckDate,
                  endCheckCreatedAt:
                    result?.endCheckCreatedAt || endCheckDate,
                }
              : user
          )
        );

        setManagers((prev) =>
          prev.map((user) =>
            resolveRecordIdentifier(user) === recordId
              ? {
                  ...user,
                  endCheck: result?.endCheck ?? true,
                  endCheckDate: result?.endCheckDate || endCheckDate,
                  endCheckCreatedAt:
                    result?.endCheckCreatedAt || endCheckDate,
                }
              : user
          )
        );

        setEndStatus({
          type: 'success',
          message: 'تم تأكيد الإكمال النهائي بنجاح.',
        });
      } catch (error) {
        setEndStatus({
          type: 'error',
          message: error.message || 'تعذر تأكيد الإكمال النهائي.',
        });
      } finally {
        setConfirmingEndId(null);
      }
    },
    [token]
  );

  const clearSelection = useCallback(() => {
    resetState();
  }, [resetState]);

  return {
    selectedSchool,
    users,
    managers,
    total,
    managersTotal,
    status,
    managersStatus,
    actionStatus,
    confirmingId,
    nfcStatus,
    nfcUpdatingId,
    emailStatus,
    confirmingEmailId,
    paperCatStatus,
    confirmingPaperCatId,
    endStatus,
    confirmingEndId,
    selectSchool: fetchUsers,
    fetchManagers,
    confirmPrinting,
    updateNfcId,
    confirmAddEmail,
    confirmPaperCat,
    confirmEndAll,
    clearSelection,
  };
}

